import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useContext,
} from "react";
import Button from "../../../Common/Button/Button";
import "./JobsTableList.css";
import Table from "../../../Common/Table/Table";
import { getStageColor } from "../../../Common/constants/stageColors";
import HorizontalTabs from "../../../Common/Horizontal-Tabs/HorizontalTabs.js";
import {
  formatDate,
  formatInterviewerName,
} from "../../../Common/Common-Function/CommonFunctions.js";
import KanbanBoard from "../../../Common/KanbanBoard/KanbanBoard.js";
import { DynamicContext } from "../../../Context/DynamicContext.js";
import { ReactComponent as ColumnsIcon } from "../../../Assets/Images/pepicons-pop_list.svg";
import { ReactComponent as ListIcon } from "../../../Assets/Images/gravity-ui_square-chart-column.svg";
import { get } from "../../../helper/Api_helper.js";
import SidePopup from "../../../Common/SidePopup/SidePopup";
import CandidateDetailsContainer from "../../../Common/Candidate-Details-Container/CandidateDetailsContainer";
import ClientEmailPopup from "../Candidates/ClientEmailPopup.js";
import { Toast } from "../../../Common/Toast-Snackbar/ToastSnackbar.js";
import AssignedCandidatesList from "./AssignedCandidatesList.js";
import { ReactComponent as TableIcon } from "../../../Assets/Images/table_icon.svg";
import CustomTooltip from "../../../Common/Tooltip/Tooltip.js";
import { post } from "../../../helper/Api_helper.js";
import VybogTALLoaderAnimation from "../../../Common/Dynamic-Animations/DynamicAnimations.js";

import {
  VYLABS_BASE_URL,
  GET_CANDIDATE_BY_ID,
  GET_APPLICATION_BY_JOB,
  GET_RECRUITER_BY_ID,
  GET_VENDOR_DETAILS_BY_ID,
  UPDATE_CANDIDATE_DETAILS,
  VENDORS_SEARCH,
} from "../../../helper/url_helper.js";
import trackedNotSelectedIcon from "../../../Assets/Images/tracked-not-selected-icon.svg";
import trackedSelectedIcon from "../../../Assets/Images/tracked-selected-icon.svg";
import { fetchCallStages } from "../../../Common/DynamicCallStage/DynamicCallStageMapping.js";

function AssignedCandidates({ job, onView, onAssignedCountChange }) {
  const { setIsFromJobRecommendedCandidates, MemberAccess } =
    useContext(DynamicContext);
  const [viewType, setViewType] = useState("kanban");
  const [assignedCandidates, setAssignedCandidates] = useState([]);
  const [recruiterNames, setRecruiterNames] = useState({});
  const [stageCounts, setStageCounts] = useState({});
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isCandidateDetailsOpen, setIsCandidateDetailsOpen] = useState(false);
  const [candidateDetailsLoading, setCandidateDetailsLoading] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const dropdownBtnRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [isEditingCandidate, setIsEditingCandidate] = useState(false);
  const [originalCandidateData, setOriginalCandidateData] = useState(null);
  const [editedCandidateData, setEditedCandidateData] = useState(null);
  const [vendorList, setVendorList] = useState([]);
  const [vendorOptionLoading, setVendorOptionLoading] = useState(false);
  const [assignedVendor, setAssignedVendor] = useState(null);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedCandidatesForEmail, setSelectedCandidatesForEmail] = useState(
    [],
  );
  const [isClientEmailPopupOpen, setIsClientEmailPopupOpen] = useState(false);
  const [clearRowsToggle, setClearRowsToggle] = useState(false);
  const [isTrackedByMe, setIsTrackedByMe] = useState(false);
  const [callStageMap, setCallStageMap] = useState({});

  useEffect(() => {
    const loadCallStages = async () => {
      const stages = await fetchCallStages();
      setCallStageMap(stages);
    };

    loadCallStages();
  }, []);

  const canSelect = useMemo(() => {
    return assignedCandidates.some((app) => app.stage !== "");
  }, [assignedCandidates]);

  const handleToggleSelectMode = () => {
    setIsSelectMode((prev) => {
      if (prev) {
        setSelectedCandidatesForEmail([]);
        setClearRowsToggle((t) => !t);
      }
      return !prev;
    });
  };

  const normalizeSelectedCandidate = useCallback(
    (candidate, fallbackCandidateId) => {
      if (!candidate) return candidate;
      const normalizedId =
        candidate.id ??
        candidate.candidate_id ??
        candidate.candidateId ??
        fallbackCandidateId;
      return normalizedId ? { ...candidate, id: normalizedId } : candidate;
    },
    [],
  );

  const handleCardSelect = useCallback(
    (candidate) => {
      setSelectedCandidatesForEmail((prev) => {
        const normalized = normalizeSelectedCandidate(candidate);
        const candidateId = normalized?.id;
        const candidateKey = String(candidateId);
        const isSelected = prev.some((c) => String(c.id) === candidateKey);
        return isSelected
          ? prev.filter((c) => String(c.id) !== candidateKey)
          : [...prev, normalized];
      });
    },
    [normalizeSelectedCandidate],
  );

  const handleSelectedRowsChange = useCallback(
    ({ selectedRows }) => {
      const selectedTableCandidates = selectedRows
        .map((row) =>
          normalizeSelectedCandidate(row.candidate, row.candidate_id),
        )
        .filter(Boolean);
      setSelectedCandidatesForEmail(selectedTableCandidates);
    },
    [normalizeSelectedCandidate],
  );

  const handleSendEmail = () => {
    if (selectedCandidatesForEmail.length === 0) {
      Toast("error", "Please select at least one candidate.");
      return;
    }
    setIsClientEmailPopupOpen(true);
  };

  const handleViewCandidate = useCallback(
    async (candidateId, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (!candidateId && candidateId !== 0) {
        console.error("Invalid candidateId:", candidateId);
        return;
      }

      setCandidateDetailsLoading(true);
      setActiveTab(0);

      try {
        const res = await get(
          VYLABS_BASE_URL,
          `${GET_CANDIDATE_BY_ID}?id=${candidateId}`,
        );
        const candidate = res?.candidate;
        const candidatefiles = res?.files;
        const parsedResumeData = candidate?.parsed_resume
          ? JSON.parse(candidate.parsed_resume)
          : null;
        const presignedUrl =
          candidatefiles && candidatefiles.length > 0
            ? candidatefiles[0].presigned_url
            : null;
        const updatedCandidate = {
          ...candidate,
          secondaryEmail: candidate.secondary_email,
          secondaryPhone: candidate.secondary_phone,
          vendorID: candidate.vendor_id,
          lineAddress1: candidate.line_address_1,
          lineAddress2: candidate.line_address_2,
          preferredSalary: candidate.preferred_salary,
          zipCode: candidate.zip_code,
          parsedResumeData,
          files: candidatefiles || null,
          presignedUrl,
          isFileBased: false,
          avatar: res?.profile_img_url,
        };
        if (updatedCandidate.vendorID) {
          try {
            const vendorResponse = await get(
              VYLABS_BASE_URL,
              `${GET_VENDOR_DETAILS_BY_ID}?id=${updatedCandidate.vendorID}`,
            );
            setAssignedVendor(vendorResponse);
            updatedCandidate.vendorDetails = vendorResponse?.vendor || null;
          } catch (err) {
            console.error("Error fetching vendor details:", err);
            updatedCandidate.vendorDetails = null;
            setAssignedVendor(null);
          }
        } else {
          updatedCandidate.vendorDetails = null;
          setAssignedVendor(null);
        }
        setSelectedCandidate(updatedCandidate);
        setIsFromJobRecommendedCandidates(true);
        setIsCandidateDetailsOpen(true);
      } catch (error) {
        console.error("Error fetching candidate details:", error);
        Toast("error", "Failed to fetch candidate details");
        setSelectedCandidate(null);
      } finally {
        setCandidateDetailsLoading(false);
      }
    },
    [setAssignedVendor, setIsFromJobRecommendedCandidates],
  );

  const handleCloseCandidateDetails = () => {
    setIsCandidateDetailsOpen(false);
    setSelectedCandidate(null);
    resetCandidateEdit();
    setIsFromJobRecommendedCandidates(false);
  };

  const fetchAssignedCandidates = async (trackedOverride) => {
    const tracked =
      typeof trackedOverride === "boolean" ? trackedOverride : isTrackedByMe;
    try {
      setIsLoading(true);
      let allApps = [];
      let offset = 0;
      const limit = 50;
      let hasMore = true;
      let initialResponse = null;

      while (hasMore) {
        const trackedParam = tracked ? "&filter_by=tracked" : "";
        const response = await get(
          VYLABS_BASE_URL,
          `${GET_APPLICATION_BY_JOB}?job_id=${job.id}&limit=${limit}&offset=${offset}${trackedParam}`,
        );
        if (!initialResponse) initialResponse = response;
        const apps = response?.applications || [];
        allApps = [...allApps, ...apps];

        if (apps.length < limit) {
          hasMore = false;
        } else {
          offset += limit;
        }
      }

      setAssignedCandidates(allApps);
      const counts = {};
      if (initialResponse?.stages_count) {
        initialResponse.stages_count.forEach((item) => {
          counts[item.stage] = item.count;
        });
      }
      setStageCounts(counts);

      const recruiterIds = new Set();
      allApps.forEach((a) => {
        if (a.assigned_recruiter_id) recruiterIds.add(a.assigned_recruiter_id);
      });

      const idsToFetch = Array.from(recruiterIds).filter(
        (rid) => !recruiterNames[rid],
      );
      const recruiterRequests = idsToFetch.map(async (recruiterId) => {
        try {
          const res = await get(
            VYLABS_BASE_URL,
            `${GET_RECRUITER_BY_ID}?id=${recruiterId}`,
          );
          return {
            id: recruiterId,
            name: res?.user?.name,
          };
        } catch (err) {
          console.error("Failed to fetch recruiter:", err);
          return null;
        }
      });
      const recruiterResults = await Promise.all(recruiterRequests);
      setRecruiterNames((prev) => {
        const next = { ...prev };
        recruiterResults.forEach((r) => {
          if (r?.id && r?.name) {
            next[r.id] = formatInterviewerName(r.name);
          }
        });
        return next;
      });
    } catch (err) {
      console.error("GET JOBS_LIST error", err);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (!job || !job.id) {
      setIsLoading(false);
      return;
    }
    fetchAssignedCandidates();
  }, [job, isTrackedByMe]);

  const handleToggleTrackByMe = () => {
    setIsTrackedByMe((prev) => !prev);
  };

  const handleTabChange = (index) => {
    setActiveTab(index);
  };

  const resetCandidateEdit = () => {
    if (originalCandidateData) {
      setSelectedCandidate(originalCandidateData);
    }
    setIsEditingCandidate(false);
    setOriginalCandidateData(null);
    setEditedCandidateData(null);
  };

  const handleEditCandidate = () => {
    const getValidExperience = (value) => {
      const num = Number(value);

      if (isNaN(num)) return "";
      if (num < 0) return 0;
      return num;
    };

    let experienceValue = "";
    const directExp = getValidExperience(
      selectedCandidate?.years_of_experience,
    );

    const parsedExp = getValidExperience(
      selectedCandidate?.parsedResumeData?.BasicInfo?.YearsOfExperience,
    );

    if (directExp !== "") {
      experienceValue = directExp;
    } else if (parsedExp !== "") {
      experienceValue = parsedExp;
    }
    const candidateWithFormattedSkills = {
      ...selectedCandidate,
      name: selectedCandidate.name || selectedCandidate.BasicInfo?.Name || "",
      email:
        selectedCandidate.email || selectedCandidate.BasicInfo?.Email || "",
      phone:
        selectedCandidate.phone ||
        selectedCandidate.BasicInfo?.PhoneNumber ||
        "",
      skills: Array.isArray(selectedCandidate.skills)
        ? selectedCandidate.skills
        : selectedCandidate.parsedResumeData?.Skills || [],
      designation:
        selectedCandidate.designation?.trim() ||
        selectedCandidate.parsedResumeData?.CareerHistory?.JobDetails?.[0]
          ?.JobTitle ||
        "",
      experience: experienceValue,
      lineAddress1:
        selectedCandidate.lineAddress1 ||
        selectedCandidate.BasicInfo?.LineAddress1 ||
        "",
      lineAddress2:
        selectedCandidate.lineAddress2 ||
        selectedCandidate.BasicInfo?.LineAddress2 ||
        "",
      zipCode:
        selectedCandidate.zipCode || selectedCandidate.BasicInfo?.ZipCode || "",
      preferredSalary:
        selectedCandidate.preferredSalary ||
        selectedCandidate.BasicInfo?.PreferredSalary ||
        "",
    };
    setOriginalCandidateData({ ...selectedCandidate });
    setEditedCandidateData(candidateWithFormattedSkills);
    setIsEditingCandidate(true);
  };

  const handleCandidateChange = (data) => {
    setEditedCandidateData((prev) => ({ ...prev, ...data }));
  };

  const saveCandidateChanges = async () => {
    if (!editedCandidateData) {
      Toast("error", "No changes to save");
      return;
    }

    try {
      const candidateData = { id: selectedCandidate.id };
      Object.keys(editedCandidateData).forEach((key) => {
        if (editedCandidateData[key] !== selectedCandidate[key]) {
          candidateData[key] = editedCandidateData[key];
        }
      });

      if (Object.keys(candidateData).length === 1) {
        Toast("info", "No changes were made.");
        setIsEditingCandidate(false);
        return;
      }

      Object.keys(candidateData).forEach((key) => {
        if (key === "preferredSalary") {
          candidateData[key] = Number(candidateData[key]);
        }
      });

      if (candidateData.experience !== undefined) {
        candidateData.yearsOfExperience = Number(candidateData.experience);
        delete candidateData.experience;
      }

      const response = await post(
        VYLABS_BASE_URL,
        UPDATE_CANDIDATE_DETAILS,
        candidateData,
      );

      if (response) {
        const updatedCandidate = { ...selectedCandidate, ...candidateData };
        setSelectedCandidate(updatedCandidate);
        setIsEditingCandidate(false);
        setOriginalCandidateData(null);
        setEditedCandidateData(null);
        Toast("success", "Candidate updated successfully");
      }
    } catch (error) {
      console.error("Error updating candidate:", error);
      Toast("error", "Failed to update candidate");
    }
  };

  const fetchVendors = async (query) => {
    try {
      setVendorOptionLoading(true);
      const res = await get(
        VYLABS_BASE_URL,
        `${VENDORS_SEARCH}?vendor_name=${encodeURIComponent(query)}`,
      );
      setVendorList(
        (res.vendors || [])
          .filter((v) => v.id && v.name)
          .map((v) => ({
            label: v.contact_person
              ? `${v.name} - ${v.contact_person}`
              : v.name,
            value: v.id,
          })),
      );
    } catch {
      setVendorList([]);
    } finally {
      setVendorOptionLoading(false);
    }
  };

  const handleVendorInputChange = (_, value) => {
    if (value && value.length >= 3) fetchVendors(value);
    else setVendorList([]);
  };

  const resolveCandidateId = (row) => {
    if (row?.candidate?.id) return row.candidate.id;
    if (row?.candidateId) return row.candidateId;
    if (row?.candidate_id) return row.candidate_id;
    return null;
  };

  const columnsDefinition = useMemo(
    () => [
      {
        name: "Candidate Name",
        selector: (row) => (
          <>
            <h4>{row?.candidate?.name ?? "N/A"}</h4>
            <p>{row?.jobTitle ?? ""}</p>
          </>
        ),
      },
      {
        name: "Candidate Email",
        selector: (row) => (
          <>
            <h4>{row?.candidate?.email ?? "N/A"}</h4>
            <p>{row?.jobTitle ?? ""}</p>
          </>
        ),
      },
      {
        name: "Candidate Contact",
        selector: (row) => (
          <span className="candidate-score">
            {row?.candidate?.phone ?? "N/A"}
          </span>
        ),
      },
      {
        name: "Stage",
        selector: (row) => {
          const stage = row?.stage ?? "N/A";
          const stageName = callStageMap[stage] || stage;

          return (
            <div className="stage-button">
              <Button
                style={{ padding: "4px 8px", pointerEvents: "none" }}
                label={stageName}
                backgroundColor="#6860ee"
              />
            </div>
          );
        },
      },
      {
        name: "Assigned By",
        selector: (row) => (
          <span className="candidate-score">
            {recruiterNames[row?.assigned_recruiter_id] ?? "N/A"}
          </span>
        ),
      },
      {
        name: "Assigned On",
        selector: (row) => (
          <span className="candidate-score">
            {row?.created_at ? formatDate(new Date(row.created_at)) : "N/A"}
          </span>
        ),
      },
      {
        name: "Actions",
        cell: (row) => {
          const candidateId = resolveCandidateId(row);

          const handleViewClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            handleViewCandidate(candidateId, e);
          };

          return (
            <Button
              label="View"
              event={handleViewClick}
              backgroundColor="var(--body__background)"
              color="#6860EE"
              border="1px solid #6860EE"
              style={{ padding: "8px 12px" }}
            />
          );
        },
      },
    ],
    [recruiterNames, handleViewCandidate, callStageMap],
  );

  const columnList = useMemo(
    () => columnsDefinition.map((c) => c.name),
    [columnsDefinition],
  );

  useEffect(() => {
    setVisibleColumns(columnList);
  }, [columnList]);

  useEffect(() => {
    if (!dropdownOpen) return;
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        dropdownBtnRef.current &&
        !dropdownBtnRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mouseup", handleClickOutside);
    return () => document.removeEventListener("mouseup", handleClickOutside);
  }, [dropdownOpen]);

  return (
    <div className="assign-candidate-outer">
      {!isLoading && (
        <div className="assign-candidate-header">
          <HorizontalTabs
            activeTab={["kanban", "list", "table"].indexOf(viewType)}
            handleTabChange={(index) => {
              const views = ["kanban", "list", "table"];
              const newViewType = views[index];
              if (viewType !== newViewType) {
                setSelectedCandidatesForEmail([]);
                setIsSelectMode(false);
              }
              setViewType(newViewType);
            }}
            tabNames={[
              { name: "Board", icon: <ListIcon /> },
              { name: "List", icon: <ColumnsIcon /> },
              {
                name: "Table",
                icon: <TableIcon style={{ height: "16px", width: "16px" }} />,
              },
            ]}
            tabstyle={{
              marginBottom: "0",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
            customActiveClass="job-tab-active"
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginLeft: "auto",
              ...(viewType === "kanban" && { marginRight: "160px" }),
            }}
          >
              <CustomTooltip
                title={
                  isTrackedByMe
                    ? "Showing candidates tracked by me"
                    : "Show only candidates tracked by me"
                }
                arrow
              >
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  <Button
                    label={isTrackedByMe ? "Show All" : "Tracked By Me"}
                    iconSrc={
                      isTrackedByMe
                        ? trackedSelectedIcon
                        : trackedNotSelectedIcon
                    }
                    iconPosition="left"
                    iconStyle={{ marginRight: "8px" }}
                    event={handleToggleTrackByMe}
                    className={`track-can-btn ${isTrackedByMe ? "active" : ""}`}
                  />
                </span>
              </CustomTooltip>
            {!MemberAccess && canSelect ? (
              !isSelectMode ? (
                <CustomTooltip title="Select candidates for client email" arrow>
                  <span style={{ display: "inline-flex", alignItems: "center" }}>
                    <Button
                      label="Select Candidates"
                      event={handleToggleSelectMode}
                      style={{
                        backgroundColor: "#6665ea",
                        color: "#fff",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    />
                  </span>
                </CustomTooltip>
              ) : (
                <>
                  <span style={{ display: "inline-flex", alignItems: "center" }}>
                    <Button
                      label="Cancel"
                      event={handleToggleSelectMode}
                      isCancel
                    />
                  </span>
                  <CustomTooltip
                    title="Send email for selected candidates"
                    arrow
                  >
                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                      <Button
                        label={`Send Email (${selectedCandidatesForEmail.length})`}
                        event={handleSendEmail}
                        disabled={selectedCandidatesForEmail.length === 0}
                        style={{
                          backgroundColor:
                            selectedCandidatesForEmail.length > 0
                              ? "#6665ea"
                              : "var(--disabled-button)",
                          color: "#fff",
                          cursor:
                            selectedCandidatesForEmail.length > 0
                              ? "pointer"
                              : "not-allowed",
                          whiteSpace: "nowrap",
                        }}
                      />
                    </span>
                  </CustomTooltip>
                </>
              )
            ) : null}
          </div>
        </div>
      )}
      {isLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
          }}
        >
          <VybogTALLoaderAnimation />
        </div>
      ) : (
        <>
          {viewType === "kanban" && (
            <KanbanBoard
              key={"kanbanboard"}
              jobId={job.id}
              onTotalCount={onAssignedCountChange}
              applications={assignedCandidates}
              counts={stageCounts}
              onRefresh={fetchAssignedCandidates}
              isSelectMode={isSelectMode}
              selectedCandidates={selectedCandidatesForEmail}
              onSelectCandidate={handleCardSelect}
            />
          )}
          {viewType === "list" && (
            <AssignedCandidatesList
              applications={assignedCandidates}
              recruiterNames={recruiterNames}
              onCardView={handleViewCandidate}
              stageCounts={stageCounts}
              isSelectMode={isSelectMode}
              selectedCandidates={selectedCandidatesForEmail}
              onSelectCandidate={handleCardSelect}
            />
          )}
          {viewType === "table" && (
            <Table
              columns={columnsDefinition.filter((c) =>
                visibleColumns.includes(c.name),
              )}
              data={assignedCandidates}
              selectableRows={isSelectMode}
              selectableRowDisabled={(row) => row.stage === ""}
              onSelectedRowsChange={handleSelectedRowsChange}
              selectableRowSelected={(row) =>
                selectedCandidatesForEmail.some(
                  (c) => String(c.id) === String(row.candidate_id),
                )
              }
              clearSelectedRows={clearRowsToggle}
              pagination
              highlightOnHover
              fixedHeader
              fixedHeaderScrollHeight="300px"
              backgroundColor="var(--body__background)"
              color="var(--header__color)"
              dataNotFoundMessage="No candidates assigned yet."
            />
          )}
        </>
      )}

      {isCandidateDetailsOpen && (
        <SidePopup
          isOpen={isCandidateDetailsOpen}
          onClose={handleCloseCandidateDetails}
          title="Candidate Details"
          sidePopupWidth="85%"
        >
          {candidateDetailsLoading ? (
            <div style={{ padding: "40px", textAlign: "center" }}>
              Loading...
            </div>
          ) : selectedCandidate ? (
            <div className="candidate-details-wrapper">
              <CandidateDetailsContainer
                candidate={selectedCandidate}
                activeTab={activeTab}
                handleTabChange={handleTabChange}
                hasSearched={true}
                hasRecommendedCandidates={false}
                isEditingCandidate={isEditingCandidate}
                handleEditCandidate={handleEditCandidate}
                saveCandidateChanges={saveCandidateChanges}
                resetCandidateEdit={resetCandidateEdit}
                handleCandidateChange={handleCandidateChange}
                editedCandidateData={editedCandidateData}
                vendorList={vendorList}
                vendorOptionLoading={vendorOptionLoading}
                handleVendorInputChange={handleVendorInputChange}
                assignedVendor={assignedVendor}
                showCandidateInterviewTab={false}
                showCandidateRecommendedJobsTab={false}
              />
            </div>
          ) : (
            <div style={{ padding: "40px", textAlign: "center" }}>
              No candidate selected.
            </div>
          )}
        </SidePopup>
      )}
      <ClientEmailPopup
        isOpen={isClientEmailPopupOpen}
        onClose={() => setIsClientEmailPopupOpen(false)}
        selectedCandidates={selectedCandidatesForEmail}
        jobId={job?.id}
        jobTitle={job?.title}
      />
    </div>
  );
}

export default AssignedCandidates;